/**
 * EquipmentRentalFlow - Equipment hire with inventory, delivery, and damage deposits
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { 
  Calendar as CalendarIcon, Clock, Package, Check, ChevronRight, Loader2,
  MapPin, Truck, Shield, AlertTriangle, Minus, Plus, Info
} from 'lucide-react';
import { format, addDays, differenceInDays } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Equipment categories with sample inventory
const EQUIPMENT_CATEGORIES = [
  { id: 'tents', name: 'Tents & Marquees', icon: '⛺' },
  { id: 'tables', name: 'Tables', icon: '🪑' },
  { id: 'chairs', name: 'Chairs', icon: '💺' },
  { id: 'linens', name: 'Table Linens', icon: '🎀' },
  { id: 'lighting', name: 'Lighting', icon: '💡' },
  { id: 'sound', name: 'Sound Systems', icon: '🔊' },
  { id: 'staging', name: 'Staging & Dance Floor', icon: '🎭' },
  { id: 'catering_equip', name: 'Catering Equipment', icon: '🍽️' }
];

export default function EquipmentRentalFlow({ open, onClose, provider, onBookingComplete }) {
  const [step, setStep] = useState(1);
  const [selectedItems, setSelectedItems] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [deliveryOption, setDeliveryOption] = useState('delivery');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [contactInfo, setContactInfo] = useState({ name: '', phone: '', email: '' });
  const [acceptDamagePolicy, setAcceptDamagePolicy] = useState(false);
  const [loading, setLoading] = useState(false);

  // Sample inventory (in production, fetch from API)
  const inventory = provider?.inventory || [
    { id: 'tent_10x10', name: '10x10m Marquee Tent', category: 'tents', price_per_day: 15000, stock: 5, damage_deposit: 25000 },
    { id: 'tent_6x6', name: '6x6m Party Tent', category: 'tents', price_per_day: 8000, stock: 10, damage_deposit: 15000 },
    { id: 'round_table', name: 'Round Table (8-seater)', category: 'tables', price_per_day: 500, stock: 50, damage_deposit: 2000 },
    { id: 'rect_table', name: 'Rectangular Table (6-seater)', category: 'tables', price_per_day: 400, stock: 30, damage_deposit: 1500 },
    { id: 'tiffany_chair', name: 'Tiffany Chair (Gold)', category: 'chairs', price_per_day: 150, stock: 200, damage_deposit: 500 },
    { id: 'plastic_chair', name: 'Plastic Chair (White)', category: 'chairs', price_per_day: 50, stock: 500, damage_deposit: 100 },
    { id: 'tablecloth', name: 'Premium Tablecloth', category: 'linens', price_per_day: 200, stock: 100, damage_deposit: 500 },
    { id: 'fairy_lights', name: 'Fairy Lights (100m)', category: 'lighting', price_per_day: 1000, stock: 20, damage_deposit: 3000 },
    { id: 'uplights', name: 'LED Uplights (set of 10)', category: 'lighting', price_per_day: 2500, stock: 10, damage_deposit: 5000 },
    { id: 'speaker_small', name: 'Portable Speaker System', category: 'sound', price_per_day: 3000, stock: 8, damage_deposit: 10000 },
    { id: 'dance_floor', name: 'Dance Floor (4x4m)', category: 'staging', price_per_day: 5000, stock: 5, damage_deposit: 15000 },
    { id: 'chafing_dish', name: 'Chafing Dish Set', category: 'catering_equip', price_per_day: 300, stock: 30, damage_deposit: 1000 }
  ];

  const currency = provider?.country === 'UK' ? '£' : 'Rs';
  const deliveryFee = provider?.country === 'UK' ? 50 : 5000;

  // Calculate rental days
  const rentalDays = startDate && endDate ? Math.max(1, differenceInDays(endDate, startDate) + 1) : 0;

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let totalDamageDeposit = 0;

    Object.entries(selectedItems).forEach(([itemId, qty]) => {
      if (qty > 0) {
        const item = inventory.find(i => i.id === itemId);
        if (item) {
          subtotal += item.price_per_day * qty * rentalDays;
          totalDamageDeposit += item.damage_deposit * qty;
        }
      }
    });

    const delivery = deliveryOption === 'delivery' ? deliveryFee : 0;
    const total = subtotal + delivery;

    return { subtotal, delivery, totalDamageDeposit, total, grandTotal: total + totalDamageDeposit };
  };

  const updateItemQty = (itemId, delta) => {
    const item = inventory.find(i => i.id === itemId);
    if (!item) return;

    setSelectedItems(prev => {
      const currentQty = prev[itemId] || 0;
      const newQty = Math.max(0, Math.min(item.stock, currentQty + delta));
      if (newQty === 0) {
        const { [itemId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [itemId]: newQty };
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const totals = calculateTotals();
      const bookingData = {
        provider_id: provider?.id,
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd'),
        rental_days: rentalDays,
        items: Object.entries(selectedItems).map(([itemId, qty]) => {
          const item = inventory.find(i => i.id === itemId);
          return {
            item_id: itemId,
            name: item?.name,
            quantity: qty,
            price_per_day: item?.price_per_day,
            damage_deposit: item?.damage_deposit
          };
        }),
        delivery_option: deliveryOption,
        delivery_address: deliveryOption === 'delivery' ? deliveryAddress : null,
        delivery_time: deliveryOption === 'delivery' ? deliveryTime : null,
        pickup_time: pickupTime,
        special_instructions: specialInstructions,
        subtotal: totals.subtotal,
        delivery_fee: totals.delivery,
        damage_deposit: totals.totalDamageDeposit,
        total: totals.total,
        grand_total: totals.grandTotal,
        contact: contactInfo,
        damage_policy_accepted: acceptDamagePolicy
      };

      const response = await fetch(`${API_URL}/api/events/bookings/equipment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        toast.success('Equipment rental booked! You\'ll receive confirmation shortly.');
        onBookingComplete?.();
        onClose();
      } else {
        throw new Error('Booking failed');
      }
    } catch (error) {
      toast.error('Failed to complete booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();
  const selectedCount = Object.values(selectedItems).reduce((a, b) => a + b, 0);

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Select Equipment</h3>
            <p className="text-sm text-gray-500">Choose items and quantities from available inventory</p>

            <div className="max-h-[400px] overflow-y-auto space-y-4">
              {EQUIPMENT_CATEGORIES.map(category => {
                const categoryItems = inventory.filter(i => i.category === category.id);
                if (categoryItems.length === 0) return null;

                return (
                  <div key={category.id} className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 font-medium flex items-center gap-2">
                      <span>{category.icon}</span>
                      {category.name}
                    </div>
                    <div className="divide-y">
                      {categoryItems.map(item => (
                        <div key={item.id} className="p-3 flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{item.name}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span>{currency} {item.price_per_day.toLocaleString()}/day</span>
                              <span>•</span>
                              <span className={item.stock > 10 ? 'text-green-600' : 'text-amber-600'}>
                                {item.stock} available
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateItemQty(item.id, -1)}
                              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                              disabled={!selectedItems[item.id]}
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-8 text-center font-medium">
                              {selectedItems[item.id] || 0}
                            </span>
                            <button
                              onClick={() => updateItemQty(item.id, 1)}
                              className="w-8 h-8 rounded-full bg-amber-100 hover:bg-amber-200 flex items-center justify-center"
                              disabled={selectedItems[item.id] >= item.stock}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedCount > 0 && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-amber-800 font-medium">{selectedCount} items selected</p>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Rental Period</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Start Date</label>
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={(date) => {
                    setStartDate(date);
                    if (endDate && date > endDate) setEndDate(null);
                  }}
                  disabled={(date) => date < new Date()}
                  className="rounded-md border"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">End Date</label>
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={setEndDate}
                  disabled={(date) => date < (startDate || new Date())}
                  className="rounded-md border"
                />
              </div>
            </div>

            {startDate && endDate && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-blue-800">
                  <strong>{rentalDays} day{rentalDays > 1 ? 's' : ''}</strong> rental
                  ({format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')})
                </p>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Delivery & Collection</h3>

            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="delivery"
                  checked={deliveryOption === 'delivery'}
                  onChange={() => setDeliveryOption('delivery')}
                  className="w-4 h-4"
                />
                <Truck className="w-5 h-5 text-amber-600" />
                <div className="flex-1">
                  <p className="font-medium">Delivery & Collection</p>
                  <p className="text-sm text-gray-500">We deliver and collect from your venue</p>
                </div>
                <span className="font-bold text-amber-600">+{currency} {deliveryFee.toLocaleString()}</span>
              </label>

              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="delivery"
                  checked={deliveryOption === 'pickup'}
                  onChange={() => setDeliveryOption('pickup')}
                  className="w-4 h-4"
                />
                <MapPin className="w-5 h-5 text-gray-600" />
                <div className="flex-1">
                  <p className="font-medium">Self Pickup & Return</p>
                  <p className="text-sm text-gray-500">Collect from our warehouse</p>
                </div>
                <span className="font-bold text-green-600">Free</span>
              </label>
            </div>

            {deliveryOption === 'delivery' && (
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-700">Delivery Address</label>
                  <Textarea
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    placeholder="Full venue address for delivery"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Preferred Delivery Time</label>
                  <Select value={deliveryTime} onValueChange={setDeliveryTime}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Morning (8am - 12pm)</SelectItem>
                      <SelectItem value="afternoon">Afternoon (12pm - 5pm)</SelectItem>
                      <SelectItem value="evening">Evening (5pm - 8pm)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-700">Collection/Return Time</label>
              <Select value={pickupTime} onValueChange={setPickupTime}>
                <SelectTrigger>
                  <SelectValue placeholder="When should we collect?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="same_day_evening">Same day evening</SelectItem>
                  <SelectItem value="next_morning">Next morning (before 12pm)</SelectItem>
                  <SelectItem value="next_afternoon">Next afternoon</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Special Instructions</label>
              <Textarea
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                placeholder="Any setup requirements, access instructions..."
                rows={2}
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Contact & Confirm</h3>

            <div className="space-y-3">
              <Input
                placeholder="Your full name *"
                value={contactInfo.name}
                onChange={(e) => setContactInfo(c => ({ ...c, name: e.target.value }))}
              />
              <Input
                placeholder="Phone number *"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo(c => ({ ...c, phone: e.target.value }))}
              />
              <Input
                type="email"
                placeholder="Email address *"
                value={contactInfo.email}
                onChange={(e) => setContactInfo(c => ({ ...c, email: e.target.value }))}
              />
            </div>

            {/* Order Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <h4 className="font-medium">Order Summary</h4>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {Object.entries(selectedItems).map(([itemId, qty]) => {
                  const item = inventory.find(i => i.id === itemId);
                  if (!item) return null;
                  return (
                    <div key={itemId} className="flex justify-between text-gray-600">
                      <span>{item.name} x{qty}</span>
                      <span>{currency} {(item.price_per_day * qty * rentalDays).toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
              <div className="border-t pt-2 space-y-1">
                <div className="flex justify-between">
                  <span>Subtotal ({rentalDays} days)</span>
                  <span>{currency} {totals.subtotal.toLocaleString()}</span>
                </div>
                {totals.delivery > 0 && (
                  <div className="flex justify-between">
                    <span>Delivery & Collection</span>
                    <span>{currency} {totals.delivery.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium">
                  <span>Rental Total</span>
                  <span>{currency} {totals.total.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-amber-700 font-medium">
                  <span className="flex items-center gap-1">
                    <Shield className="w-4 h-4" />
                    Damage Deposit (Refundable)
                  </span>
                  <span>{currency} {totals.totalDamageDeposit.toLocaleString()}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold text-lg">
                  <span>Total to Pay</span>
                  <span className="text-amber-600">{currency} {totals.grandTotal.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Damage Policy */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Damage Deposit Policy</p>
                  <p className="text-amber-700 mt-1">
                    The damage deposit is fully refundable within 7 days if all items are returned in good condition.
                    Any damages will be assessed and deducted from the deposit.
                  </p>
                </div>
              </div>
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                checked={acceptDamagePolicy}
                onCheckedChange={setAcceptDamagePolicy}
                className="mt-1"
              />
              <span className="text-sm text-gray-600">
                I accept the damage deposit policy and terms of rental
              </span>
            </label>
          </div>
        );
    }
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-amber-600" />
            Rent Equipment
          </DialogTitle>
          <DialogDescription>{provider.provider_name}</DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-4">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s < step ? 'bg-amber-600 text-white' :
                s === step ? 'bg-amber-100 text-amber-700 border-2 border-amber-600' :
                'bg-gray-100 text-gray-400'
              }`}>
                {s < step ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 4 && <div className={`flex-1 h-1 ${s < step ? 'bg-amber-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {renderStep()}

        <div className="flex justify-between pt-4 border-t">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(s => s - 1)}>Back</Button>
          ) : <div />}

          {step < 4 ? (
            <Button
              onClick={() => setStep(s => s + 1)}
              disabled={
                (step === 1 && selectedCount === 0) ||
                (step === 2 && (!startDate || !endDate)) ||
                (step === 3 && deliveryOption === 'delivery' && (!deliveryAddress || !deliveryTime))
              }
              className="bg-amber-600 hover:bg-amber-700"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading || !acceptDamagePolicy || !contactInfo.name || !contactInfo.phone || !contactInfo.email}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Confirm Booking
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
